import {
  Attribute,
  ContentChild,
  ContentChildren,
  Directive,
  Injector,
  Input,
  OnInit,
  Optional,
  TemplateRef
} from '@angular/core';

import {
  DISPLAY_TYPE,
  EDIT_MODE,
  IWidgetConfig,
  StylableComponent,
  provideAsWidgetRef,
  Parent,
  register,
  styler,
  BaseComponent,
  getDefaultValue,
  isDataSetWidget,
  getDataTableFilterWidget
} from "@wm/components-base";
import { isDateTimeType, DataType, getDisplayDateTimeFormat, isDefined, FormWidgetType, runtimeEventManager, toBoolean } from '@wm/core';
import { TableColumnGroupDirective } from '../table-column-group/table-column-group.directive';
import { TableComponent } from '../table.component';
import { getEditModeWidget } from '@wm/build-task';
import { setHeaderConfigForTable } from '@wm/components-base';
import { extend, includes, isBoolean, last, map, max, mean, min, round, set, sum } from 'lodash-es';
import { setDeviceDisplayOnWidget } from "../../../formWidgetUtils";
import { DatePipe, DecimalPipe } from "@angular/common";

const DEFAULT_CLS = '';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-table-column',
  hostClass: DEFAULT_CLS
};
let inlineWidgetProps = ['datafield', 'displayfield', 'placeholder', 'searchkey', 'matchmode', 'displaylabel', 'groupby', 'match', 'dateformat', 'showcount', 'collapsible',
  'checkedvalue', 'uncheckedvalue', 'showdropdownon', 'dataentrymode', 'autoclose', 'dataset'];
const validationProps = ['maxchars', 'regexp', 'minvalue', 'maxvalue', 'step', 'required', 'mindate', 'maxdate',
  'excludedates', 'excludedays', 'mintime', 'maxtime'];
inlineWidgetProps = [...inlineWidgetProps, ...validationProps];

class FieldDef {
  widget;

  constructor(widget: any) {
    this.widget = widget;
  }

  focus() {
    this.widget.focus();
  }

  setProperty(prop: any, newval: any) {
    // Get the scope of the current editable widget and set the value
    prop = prop === 'value' ? 'datavalue' : prop;
    this.widget[prop] = newval;
  }

  getProperty(prop: any) {
    prop = prop === 'value' ? 'datavalue' : prop;
    return this.widget[prop];
  }
}

@Directive({
  selector: '[wmTableColumn]',
  providers: [
    provideAsWidgetRef(TableColumnDirective),
    DecimalPipe, DatePipe
  ],
  standalone: true,
  exportAs: 'wmTableColumn'
})
export class TableColumnDirective extends StylableComponent implements OnInit {
  static initializeProps = register('wm-table-column');
  @ContentChildren('filterWidget') _filterInstances: any;
  @ContentChildren('inlineWidget') _inlineInstances: any;
  @ContentChildren('inlineWidgetNew') _inlineInstancesNew: any;
  @ContentChild('customExprTmpl') customExprTmpl: TemplateRef<any>;
  @ContentChild('inlineWidgetTmpl') inlineWidthTempRef: TemplateRef<any>;
  @ContentChild('filterTmpl') filterTemplateRef: TemplateRef<any>;

  widgetConfig: any = {};
  placeholder: string;
  required: boolean | string;
  limit: any;
  /**
       * CSS class for styling the column.
       */
  @Input() colClass: string;
  /**
   * Datatype the column.
   */
  @Input() type: string = 'string';
  /**
   * This property allows user to bind expression to column class property.
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() colNgClass: string;
  /**
   * The binding for the column. It is the target column in the data object the table is bound to.
   */
  @Input() binding: string;
  /**
   * This property specifies the title of the column header. <br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() caption: string;
  /**
   * This property defines the value of the widget when the element is in the checked state.<br>
   * Default value is boolean value true. If specified, the value will be of string type.
   */
  @Input() checkedvalue: string;
  /**
   * Specify the currency code such as 'USD'.
   */
  @Input() currencypattern: string;
  /**
   * This property sets the dataValue to be returned by the dataset widget when the list is populated using the dataSet property. <br>
   */
  @Input() datafield: string;
  /**
   * This property sets the filter dataValue to be returned by the dataset widget when the list is populated using the dataSet property. <br>
   */
  @Input() filterdatafield: string;
  /**
   * This property accepts the options to create the dataset widget.
   * These options can be array of values, array of objects, object containing key-value pairs. <br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() dataset: Array<any> | any;
  /**
   * This property defines the initial selected value of the dataset widget. <br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() datavalue: any;
  /**
   * This property sets the displayValue to show in the dataset widget when the list is populated using the dataSet property. <br>
   */
  @Input() displayfield: string;
  /**
   * This property sets the displayValue to show in the dataset widget when the list is populated using the dataSet property. <br>
   */
  @Input() displaylabel: string;
  /**
   * This property sets the filter displayValue to show in the dataset widget when the list is populated using the dataSet property. <br>
   */
  @Input() filterdisplayfield: string;
  /**
   * This property sets the filter displayValue to show in the dataset widget when the list is populated using the dataSet property. <br>
   */
  @Input() filterdisplaylabel: string;
  /**
   * This property defines the display pattern of column in view mode.<br>
   * <p><em>Allowed Values: </em><code>Standard Patterns like 'yyyy-MM-dd hh:mm:ss a', 'yyyy-MM-ddTHH:mm:ss', 'yyyy, MMM dd', etc. </code></p>
   */
  @Input() datepattern: string | undefined;
  /**
   * This property display pattern of the datetime widget in inline or quick edit.<br>
   * <p><em>Allowed Values: </em><code>Standard Patterns like 'yyyy-MM-dd hh:mm:ss a', 'yyyy-MM-ddTHH:mm:ss', 'yyyy, MMM dd', etc. </code></p>
   */
  @Input() editdatepattern: string;

  /**
   * Widget type in the inline or quick edit.
   */
  @Input() editWidgetType: string;
  /**
   * Expression against the column to be displayed when data is exported. E.g. (firstname + lastname). If left empty, actual value against the column will be displayed.
   */
  @Input() exportexpression: string;
  /**
   * Filters the values for the current field when the 'Filter on field' value changes.
   */
  @Input() filterOn: string;
  /**
   * Set the number of decimal places that are displayed.
   */
  @Input() fractionsize: number;
  /**
   * This property will be used to show/hide the column. <br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() show: any;
  /**
   * This property will be used to show/hide the column in edit mode<br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() showineditmode: any;
  /**
   * This property will be used to make the column non-editable. <br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() readonly: boolean = false;
  /**
   * This is the property to be searched upon
   */
  @Input() searchkey: string;
  /**
   * This property enables search on the column.
   */
  @Input() searchable: boolean = true;
  /**
   * This property determines whether the field should be displayed in search filter dropdown or not
   */
  @Input() showinfilter: any;
  /**
   * If the Value is set to default Date/Datetime/time picker will be opened on both input click and button click, else picker will be opened only button click.
   */
  @Input() showdropdownon: string = 'default';
  /**
   * This property specifies how the user can enter data.If the Value is set to default, data entry can be done either by selecting from the Date/DateTime/Time Picker or by entering manually through the keyboard, else data entry can be done only by selecting from the Date/DateTime/Time Picker.
   */
  @Input() dataentrymode: string = 'default';
  /**
   * Text to append to the start of each column data.
   */
  @Input() prefix: string;
  /**
   * Text to append to the end of each column data.
   */
  @Input() suffix: string;
  /**
   * This property will be used to disable/enable the table column on the web page. <br>
   * <p><em>Bindable: </em><code>true</code></p>
   */
  @Input() disabled: boolean;
  /**
   * This property defines the value of the widget when the element is in the unchecked state.
   */
  @Input() uncheckedvalue: string;
  /**
   * This property is to show the column in web view.
   */
  @Input() pcdisplay: string;
  /**
   * This property is to show the column in mobile view.
   */
  @Input() mobiledisplay: string;

  @Input() groupby: string;

  @Input() filterdataset: any;
  @Input() minvalue: any;
  @Input() maxvalue: any;
  @Input() showcount: any;
  @Input() sortby: any;



  private _isRowFilter: any;
  private _isInlineEditable: any;
  private _isNewEditableRow: any;

  key: any;
  filterInstance: any;
  inlineInstance: any;
  inlineInstanceNew: any;

  _propsInitialized: boolean;
  field: any;
  displayName: string;
  pcDisplay: any;
  mobileDisplay: any;
  tabletDisplay: any;
  tabletdisplay: any;
  textAlignment: any;
  textalignment: any;
  backgroundColor: any;
  textColor: any;
  textcolor: any;
  primaryKey: any;
  generator: any;
  relatedEntityName: any;
  style: any;
  class: any;
  ngclass: any;
  autoclose: any;
  _filterDataSet: any;
  filtersearchkey: any;
  filterplaceholder: any;
  isFilterDataSetBound: any
  private _datasource: any;
  private notifyForFields: any;
  private fieldValidations: any;
  private fieldValidations_new: any;
  filterwidget: any;
  maxchars: any;
  regexp: any;
  validationmessage: any;

  private syncValidators = [];
  private asyncValidators = [];
  private observeOnFields = [];
  _dataoptions: any;
  markupManager: any;
  liveWidgetUtils: any;
  editinputtype: any;
  filterinputtype: any;
  step: any;
  formatpattern: any;
  defaultvalue: any;
  rowactionsposition: any;
  sortable: any;
  caseinsensitive: any;

  customExpression: any;
  formatExpression: any;

  constructor(inj: Injector, @Optional() viewParent: Parent, public table: TableComponent, @Attribute('filterdataset.bind') public bindfilterdataset: any, @Optional() public group: TableColumnGroupDirective) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    this.liveWidgetUtils = runtimeEventManager.getService('LiveWidgetUtilsService');
    this.markupManager = runtimeEventManager.getService('markupManager');
  }

  get dataoptions() {
    return this._dataoptions;
  }

  set dataoptions(options) {
    this._dataoptions = options;
  }

  get datasource() {
    return this._datasource;
  }

  set datasource(ds) {
    this._datasource = ds;
  }

  getConfig() {
    let config = {
      field: this.getAttr('name'),
      displayName: this.getAttr('caption'),
      columns: [],
      accessroles: this.getAttr('accessroles'),
      backgroundColor: this.getAttr('backgroundcolor'),
      textAlignment: this.getAttr('textalignment') || 'center',
      class: this.getAttr('colClass') || ''
    };
    return extend(config, this);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // Set the default values
    this.populateFieldDef();
    // when column is inside a group then consider the col index and not header index.
    const colIndex = parseInt((this.getAttr('index') as string), 10);
    const headerIndex = parseInt((this.getAttr('headerindex') as string), 10);
    const fieldName = this.group && this.group.name;
    // Register column with header config to create group structure
    setHeaderConfigForTable(this.table?.headerConfig, this.getConfig(), fieldName, fieldName ? colIndex : headerIndex);

    if(this.table) {
      this.table.registerColumns(this, colIndex);

      this._isRowFilter = this.table.filtermode === 'multicolumn' && this.searchable;
      this._isInlineEditable = !this.readonly && (this.table.editmode !== EDIT_MODE.DIALOG && this.table.editmode !== EDIT_MODE.FORM);
      this._isNewEditableRow = this._isInlineEditable && this.table.editmode === EDIT_MODE.QUICK_EDIT && this.table.shownewrow;
    }

    this._propsInitialized = true;
  }

  ngAfterContentInit() {
    // register with table

    if (this._isRowFilter) {
      // Listen on the inner row filter widget and setup the widget
      const s1 = this._filterInstances.changes.subscribe((val: any) => {
        this.filterInstance = val.first && val.first.widget;
        this.setUpFilterWidget();
      });
      this.registerDestroyListener(() => s1.unsubscribe());
    }

    if (this._isInlineEditable) {
      const s2 = this._inlineInstances.changes.subscribe((val: any) => {
        // Listen on the inner inline widget and setup the widget
        this.inlineInstance = val.first && val.first.widget;
        this.fieldValidations.formwidget = val.first;
        this.table.registerFormField(this.binding, new FieldDef(this.inlineInstance));
        this.setUpInlineWidget('inlineInstance');
      });
      this.registerDestroyListener(() => s2.unsubscribe());

      if (this._isNewEditableRow) {
        const s3 = this._inlineInstancesNew.changes.subscribe((val: any) => {
          // Listen on the inner inline widget and setup the widget
          this.inlineInstanceNew = val.first && val.first.widget;
          this.fieldValidations_new.formwidget = val.first;
          this.setUpInlineWidget('inlineInstanceNew');
        });
        this.registerDestroyListener(() => s3.unsubscribe());
      }
    }
    this.registerReadyStateListener(() => {
      this.key = this.field || this.binding;
    });
    if ((this as any).table?.isdynamictable) {
      (this.table?.inlineWidgetTmpl as any)._results.push(this.inlineWidthTempRef);
    }
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    // manually listing the table column templateRef as templateRef will not be available prior.
    if (this.filterTemplateRef) {
      this.table?.renderDynamicFilterColumn(this.filterTemplateRef);
    }
  }

  // Set the props on the row filter widget
  setUpFilterWidget() {
    this.filterInstance.registerReadyStateListener(() => {
      if (isDataSetWidget(this.filterwidget)) {
        // if binding is department.deptId then field will be deptId
        const field = last(this.binding.split('.'));
        this.filterInstance.dataset = this._filterDataSet;
        this.filterInstance.datafield = this.filterdatafield || field;
        this.filterInstance.displayfield = this.filterdisplayfield || field;
        if (this.filterwidget === FormWidgetType.AUTOCOMPLETE) {
          this.filterInstance.displaylabel = this.filterdisplaylabel || field;
          this.filterInstance.searchkey = this.filtersearchkey || field;
        }
      }
      if (this.filterwidget === FormWidgetType.TIME) {
        this.filterInstance.timepattern = 'hh:mm:ss a';
      }
      this.filterInstance.placeholder = this.filterplaceholder || '';
    });
  }

  // Initialize the inline edit widget
  setUpInlineWidget(widget: any) {
    (this as any)[widget].registerReadyStateListener(() => {
      if (isDataSetWidget((this as any)['edit-widget-type'])) {
        (this as any)[widget].dataset = this.dataset;
      }
      inlineWidgetProps.forEach(key => {
        this.setInlineWidgetProp(widget, key, (this as any)[key]);
      });
      (this as any)[widget].datasource = this._datasource;
      (this as any)[widget].dataoptions = this._dataoptions;
      this.setInlineWidgetProp(widget, 'datepattern', this.editdatepattern);
    });
  }


  private _getColumnData() {
    return map(this.table.dataset, this.binding);
  }

  public aggregate = {
    sum: () => {
      return sum(this._getColumnData());
    },
    average: (precision: number = 2) => {
      return round(mean(this._getColumnData()), precision);
    },
    count: () => {
      return this._getColumnData().length;
    },
    minimum: () => {
      return min(this._getColumnData());
    },
    maximum: () => {
      return max(this._getColumnData());
    },
    percent: (value: any, precision: number = 2) => {
      return round((sum(this._getColumnData()) / value) * 100, precision);
    }
  };

  getStyleDef() {
    return `width: ${this.width || ''}; background-color: ${this.backgroundcolor || ''}; color: ${this.textcolor || ''};`;
  }

  populateFieldDef() {
    set(this, 'widgetType', this.$attrs.get('widget-type'));
    if (this.markupManager) {
      let tablecolumn: any = this.markupManager?.getActiveMarkupEl()?.find('wm-table-column[binding="' + this.$attrs.get("binding") + '"]');
      if (tablecolumn) {
        let innertag = tablecolumn[0]?.innerHTML.trim();
        if (innertag) {
          this.customExpression = tablecolumn[0]?.innerHTML.trim();
        }
        this.disabled = tablecolumn[0]?.getAttribute('disabled')
      }
    }
    this.liveWidgetUtils.setColumnConfig(this);
    this.width = this.width === 'px' ? '' : (this.width || '');
    this.field = this.binding;
    this.displayName = this.getBindedVal('caption') || this.caption || '';
    setDeviceDisplayOnWidget(this);

    // this.tabletDisplay = this.tabletdisplay;
    this.textAlignment = this.getAttr('textalignment') || 'left';
    this.backgroundColor = this.backgroundcolor;
    this.textColor = this.textcolor;
    this.primaryKey = this.nativeElement.getAttribute('primary-key');
    this.relatedEntityName = (this as any)['related-entity-name'];
    this.style = this.getStyleDef();
    this.class = (this as any)['col-class'] || this.nativeElement.getAttribute('col-class');
    this.ngclass = (this as any)['col-ng-class'] || this.nativeElement.getAttribute('col-ng-class');
    this.formatpattern = this.nativeElement.getAttribute('formatpattern') === 'toNumber' ? 'numberToString' : this.nativeElement.getAttribute('formatpattern');
    this.formatExpression = this.formatpattern;
    this.searchable = (this.type === 'blob' || this.type === 'clob') ? false : this.searchable;
    this.showinfilter = (this.nativeElement.hasAttribute('showinfilter') && !((this as any).table && (this as any).table.isdynamictable)) ? this.nativeElement.getAttribute('showinfilter') : (this.show);
    this.show =  this.getBindedVal('show') || (isDefined(this.$attrs.get('show')) ? (this.$attrs.get('show') === 'false' ? false : (this.$attrs.get('show') === 'true' || this.$attrs.get('show'))) : true);
    this.showineditmode =  this.getBindedVal('showineditmode') || (isDefined(this.$attrs.get('showineditmode')) ? (this.$attrs.get('showineditmode') === 'false' ? false : (this.$attrs.get('showineditmode') === 'true' || this.$attrs.get('showineditmode'))) : true);
    this.limit = this.limit ? +this.limit : undefined;
    this.editWidgetType = (this as any)['edit-widget-type'] = this.binding === "rowOperations" ? undefined : (this as any)['edit-widget-type'] || this.$attrs.get('edit-widget-type') || getEditModeWidget(this);
    this.filterOn = (this as any)['filter-on'];
    this.autoclose = (this as any)['autoclose'] || this.$attrs.get('autoclose');
    this.generator = this.$attrs.get('generator')
    this.readonly = isDefined(this.getAttr('readonly')) ? this.getAttr('readonly') === 'true' : ((this as any)['related-entity-name'] ? !this.nativeElement.getAttribute('primary-key') : includes(['identity', 'uniqueid', 'sequence'], this.generator));
    this.sortby = this.$attrs.get('sortby');
    this.caseinsensitive =  this.getAttr('caseinsensitive');
    this.filterdataset = this.getBindedVal('filterdataset')
    this.filterwidget = this.$attrs.get('filterwidget') || getDataTableFilterWidget(this.type || 'string');
    this.isFilterDataSetBound = !!this.bindfilterdataset;
    this.filterplaceholder = this.$attrs.get('filterplaceholder');
    this.placeholder = this.getBindedVal('placeholder') || this.placeholder;
    this.defaultvalue =  this.getBindedVal('defaultvalue') || getDefaultValue(this.defaultvalue, this.type, this.editWidgetType) ;
    this.maxchars = this.getBindedVal('maxchars');
    this.regexp = this.getBindedVal('regexp');
    this.validationmessage = this.getBindedVal('validationmessage');
    this.dataset = this.getBindedVal('dataset');
    this.required = this.getBindedVal('required') || this.required;
    this.minvalue = this.getBindedVal('minvalue') || this.minvalue;
    this.maxvalue = this.getBindedVal('maxvalue') || this.maxvalue;
    this.editinputtype = this.getAttr('editinputtype') || 'text';
    this.filterinputtype = this.getAttr('filterinputtype') || 'text';
    this.step = this.$attrs.get('step');
    this.datepattern = this.getAttr('datepattern');
    this.rowactionsposition = this.nativeElement.getAttribute('rowactionsposition') || undefined;
    this.sortable = this.getAttr('sortable') == 'false' ? false : undefined;
  }

  getBindedVal(prop: string) {
    let propName = prop + '.bind';
    let val = this.nativeElement.getAttribute(propName);
    if (val && val !== 'null') {
      return ('bind:' + val);
    }
    return this.nativeElement.getAttribute(prop) || null;
  }

  setProperty(property: any, nv: any) {
    (this as any)[property] = nv;
    switch (property) {
      case 'displayName':
        this.table.callDataGridMethod('setColumnProp', this.field, property, nv);
        break;
      default:
        this.table.redraw(true);
    }
  }

  override onPropertyChange(key: any, nv: any, ov: any) {
    if (!this._propsInitialized) {
      return;
    }
    switch (key) {
      case 'caption':
        this.displayName = nv || '';
        this.setProperty('displayName', this.displayName);
        break;
      case 'defaultvalue':
        this.defaultvalue = nv ? getDefaultValue(this.defaultvalue, this.type, this.editWidgetType) : undefined;
        break;
      case 'show':
        this.table.redraw(true);
        this.showinfilter = this.nativeElement.hasAttribute('showinfilter') ? this.nativeElement.getAttribute('showinfilter') : nv;
        break;
      case 'filterdataset':
        this._filterDataSet = nv;
        this.setFilterWidgetDataSet();
        break;
      case 'editdatepattern':
        this.setInlineWidgetProp('inlineInstance', 'datepattern', nv);
        this.setInlineWidgetProp('inlineInstanceNew', 'datepattern', nv);
        break;
      case 'showinfilter':
        this.showinfilter = nv;
        break;
      default:
        if (inlineWidgetProps.includes(key)) {
          this.setInlineWidgetProp('inlineInstance', key, nv);
          this.setInlineWidgetProp('inlineInstanceNew', key, nv);
        }
        break;
    }

    super.onPropertyChange(key, nv, ov);
  }

  // Set the data on the row filter widget
  setFilterWidgetDataSet() {
    if (this.filterInstance) {
      this.filterInstance.dataset = this._filterDataSet;
    }
  }

  // Set the props on the inline edit widget
  setInlineWidgetProp(widget: any, prop: any, nv: any) {
    if (prop === 'datepattern' && this.editWidgetType === FormWidgetType.TIME) {
      prop = 'timepattern';
    }
    if ((this as any)[widget] && isDefined(nv)) {
      (this as any)[widget][prop] = nv;
    }
  }

  // Apply default|sync|async|prop validators for inline form control
  applyValidations() {
    if (!this.getFormControl()) {
      return;
    }
    if (this.syncValidators.length > 0) {
      this.fieldValidations.setValidators(this.syncValidators);
    }
    this.fieldValidations.setUpValidators();
    if (this.asyncValidators.length > 0) {
      this.fieldValidations.setAsyncValidators(this.asyncValidators);
    }
    if (this.observeOnFields.length > 0) {
      this.fieldValidations.observeOn(this.observeOnFields, 'columns');
    }
  }

  // Remove validators for the inline widget and set form to untouched
  removeValidations() {
    this.table.ngform.markAsUntouched();
    const control = this.getFormControl();
    if (!control) {
      return;
    }
    control.clearValidators();
    control.clearAsyncValidators();
    control.updateValueAndValidity();
  }

  // Apply default|sync|async|prop validators for QuickEdit new row form control
  applyNewRowValidations() {
    if (!this._checkNewEditableRowControl()) {
      return;
    }
    if (this.syncValidators.length > 0) {
      this.fieldValidations_new.setValidators(this.syncValidators);
    }
    this.fieldValidations_new.setUpValidators();
    if (this.asyncValidators.length > 0) {
      this.fieldValidations_new.setAsyncValidators(this.asyncValidators);
    }
    if (this.observeOnFields.length > 0) {
      this.fieldValidations_new.observeOn(this.observeOnFields, 'columns');
    }
  }

  // Remove validators for the QuickEdit new row widget and set form to untouched
  removeNewRowValidations() {
    this.table.ngform.markAsUntouched();
    if (!this._checkNewEditableRowControl()) {
      return;
    }
    const control = this.getFormControl('_new');
    control.clearValidators();
    control.clearAsyncValidators();
    control.updateValueAndValidity();
  }

  getFormControl(suffix?: string) {
    const ctrlName = suffix ? (this.binding + suffix) : this.binding;
    return this.table.ngform.controls[ctrlName];
  }

  private _checkNewEditableRowControl() {
    return this._isNewEditableRow && this.getFormControl('_new');
  }

}

